// frontend/src/hooks/useUsernameValidation.js
import { useState, useCallback, useRef,useEffect } from 'react';
import { onboardingService } from '../utils/Api';
import toast from 'react-hot-toast';

const useUsernameValidation = (initialUsername = '') => {
  const [username, setUsername] = useState(initialUsername);
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null);
  const [error, setError] = useState(null);
  const [validationState, setValidationState] = useState({
    isValid: false,
    message: '',
    type: 'idle' // 'idle' | 'checking' | 'available' | 'unavailable' | 'error'
  });

  const debounceTimer = useRef(null);

  
   useEffect(() => {
    if (initialUsername && initialUsername !== username) {
      setUsername(initialUsername);
      // Resetear estado de validación cuando cambia el username externo
      setAvailable(null);
      setValidationState({
        isValid: false,
        message: '',
        type: 'idle'
      });
    }
  }, [initialUsername]);

  // Función para verificar disponibilidad
  const checkAvailability = useCallback(async (value) => {
    if (value.length < 3) {
      setValidationState({
        isValid: false,
        message: 'Mínimo 3 caracteres',
        type: 'idle'
      });
      setAvailable(null);
      return { available: false, isValid: false };
    }

    setChecking(true);
    setValidationState({
      isValid: false,
      message: 'Verificando disponibilidad...',
      type: 'checking'
    });

    try {
      const response = await onboardingService.checkUsername(value);
      const isAvailable = response.data.available;
      
      setAvailable(isAvailable);
      
      if (isAvailable) {
        setValidationState({
          isValid: true,
          message: '¡Username disponible!',
          type: 'available'
        });
        toast.success('Username disponible', { icon: '✅', duration: 2000 });
      } else {
        setValidationState({
          isValid: false,
          message: 'Este username no está disponible',
          type: 'unavailable'
        });
      }
      
      setError(null);
      return { available: isAvailable, isValid: isAvailable };
      
    } catch (err) {
      console.error('Error checking username:', err);
      setError(err.message);
      setValidationState({
        isValid: false,
        message: 'Error al verificar disponibilidad',
        type: 'error'
      });
      return { available: false, isValid: false, error: err };
      
    } finally {
      setChecking(false);
    }
  }, []);

  // Función para manejar cambios con debounce
  const handleUsernameChange = useCallback((value) => {
    // Sanitizar: solo minúsculas, números y guiones bajos
    const sanitized = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setUsername(sanitized);
    
    // Resetear estado
    setAvailable(null);
    setValidationState({
      isValid: false,
      message: sanitized.length < 3 ? 'Mínimo 3 caracteres' : '',
      type: 'idle'
    });

    // Debounce para la verificación
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (sanitized.length >= 3) {
      debounceTimer.current = setTimeout(() => {
        checkAvailability(sanitized);
      }, 500);
    }
  }, [checkAvailability]);

  // Función para actualizar username en el backend
  const updateUsername = useCallback(async (newUsername) => {
    setChecking(true);
    try {
      const response = await onboardingService.updateUsername(newUsername);
      
      setUsername(newUsername);
      setValidationState({
        isValid: true,
        message: 'Username actualizado correctamente',
        type: 'available'
      });
      
      toast.success('Username actualizado', { icon: '✅' });
      return { success: true, data: response.data };
      
    } catch (err) {
      console.error('Error updating username:', err);
      toast.error('Error al actualizar username');
      setValidationState({
        isValid: false,
        message: 'Error al actualizar',
        type: 'error'
      });
      return { success: false, error: err };
      
    } finally {
      setChecking(false);
    }
  }, []);

  // Función para resetear el estado
  const resetValidation = useCallback(() => {
    setUsername('');
    setChecking(false);
    setAvailable(null);
    setError(null);
    setValidationState({
      isValid: false,
      message: '',
      type: 'idle'
    });
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
  }, []);

  // Determinar el color y estilo para Bootstrap
  const getInputValidationState = useCallback(() => {
    if (validationState.type === 'checking') return null;
    if (validationState.type === 'available') return 'success';
    if (validationState.type === 'unavailable') return 'danger';
    if (validationState.type === 'error') return 'danger';
    return null;
  }, [validationState.type]);

  // Obtener el icono apropiado
  const getValidationIcon = useCallback(() => {
    if (validationState.type === 'checking') return 'spinner';
    if (validationState.type === 'available') return 'check';
    if (validationState.type === 'unavailable') return 'x';
    if (validationState.type === 'error') return 'alert';
    return null;
  }, [validationState.type]);

  return {
    // Estado
    username,
    checking,
    available,
    error,
    validationState,
    
    // Métodos
    handleUsernameChange,
    checkAvailability,
    updateUsername,
    resetValidation,
    
    // Helpers para UI
    getInputValidationState,
    getValidationIcon,
    
    // Props para pasar directamente al input
    inputProps: {
      value: username,
      onChange: (e) => handleUsernameChange(e.target.value),
      isInvalid: validationState.type === 'unavailable' || validationState.type === 'error',
      isValid: validationState.type === 'available',
      disabled: checking
    }
  };
};

export default useUsernameValidation;